/**
 * Scheduled vendor cost for a pricelist item (decorated by
 * GET /api/pricelists/{id}/items from price sheets held for a future
 * effective date). Odoo still holds today's cost until then.
 */
export interface UpcomingCostFields {
  upcoming_cost_price?: number | null
  upcoming_cost_uom_name?: string | null
  upcoming_cost_effective_date?: string | null
  upcoming_cost_vendor_name?: string | null
  upcoming_cost_pct_change?: number | null
  upcoming_cost_tier_count?: number | null
  upcoming_cost_tiers?: Array<{ min_qty: number; price: number | null; pct_change: number | null }> | null
}

export function hasUpcomingCost(row: UpcomingCostFields): boolean {
  return row.upcoming_cost_price != null && !!row.upcoming_cost_effective_date
}

function sameUnit(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase()
}

/**
 * Margin % at the upcoming cost, or null when it can't be compared with
 * "Our cost" (the two prices are in different units, or either is missing).
 */
export function upcomingMarginPct(
  row: UpcomingCostFields & { fixed_price: number | false; last_paid_uom_name: string | null },
): number | null {
  if (!hasUpcomingCost(row)) return null
  const price = Number(row.fixed_price)
  const cost = row.upcoming_cost_price as number
  if (!Number.isFinite(price) || price <= 0 || !Number.isFinite(cost) || cost <= 0) return null
  if (!sameUnit(row.upcoming_cost_uom_name, row.last_paid_uom_name)) return null
  return ((price - cost) / price) * 100
}

export function formatShortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
